<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useGroupContextStore } from '@/stores/groupContext'
import { useOrganizerAuthStore } from '@/stores/organizerAuth'
import AttentionCard from '@/components/AttentionCard.vue'

/**
 * A group's home screen (SPEC §8.9, §7.1).
 *
 * Two things, in the order they matter. What needs attention comes first when
 * there is any (MT-109), because an organizer opening this screen mid-week is
 * looking for what slipped rather than for the setup guide. The setup steps sit
 * underneath and are what a brand-new group sees, since SPEC §7.1 asks that an
 * organizer be shown the next thing to do rather than a blank page.
 *
 * The attention card renders nothing at all when nothing is outstanding, so a
 * settled group sees the setup steps and no empty scolding.
 */

const route = useRoute()
const groupContext = useGroupContextStore()
const auth = useOrganizerAuthStore()

const groupId = computed(() => route.params.groupId as string)
const group = computed(() => groupContext.current)

/**
 * The setup an organizer works through, in the order it has to happen.
 *
 * Households before meals because a meal with nobody to come to it is not much
 * of a meal; templates before events because an event is built from one. Each
 * card says what the step is for rather than naming the feature.
 */
const steps = computed(() => [
  {
    key: 'households',
    overline: 'First',
    title: 'Add the households',
    body: 'Each family gets a link of their own. No passwords, no accounts — they open it and they are in.',
    action: 'Add households',
    to: { name: 'organizer-households', params: { groupId: groupId.value } },
  },
  {
    key: 'templates',
    overline: 'Then',
    title: 'Describe a usual meal',
    body: 'A template is what you normally serve — the dishes, roughly how much of each. It saves repeating yourself every week.',
    action: 'Coming soon',
    to: null,
  },
  {
    key: 'event',
    overline: 'Finally',
    title: 'Plan the first one',
    body: 'Pick a date, and everyone gets asked whether they are coming and what they can bring.',
    action: 'Plan a meal',
    to: { name: 'organizer-events', params: { groupId: groupId.value } },
  },
])
</script>

<template>
  <v-container class="py-8" style="max-width: 44rem">
    <h1 class="text-h5 mb-1">
      {{ auth.displayName ? `Hello, ${auth.displayName}` : 'Your group' }}
    </h1>
    <p class="text-body-2 mb-8" style="opacity: 0.75">
      {{ group?.name ?? 'Loading…' }}
    </p>

    <AttentionCard :group-id="groupId" />

    <h2 class="text-subtitle-1 mb-3">Getting set up</h2>

    <v-card
      v-for="step in steps"
      :key="step.key"
      class="pa-5 mb-3"
      :data-testid="`setup-${step.key}`"
    >
      <div class="text-overline mb-1" style="opacity: 0.75">{{ step.overline }}</div>
      <h3 class="text-h6 mb-2">{{ step.title }}</h3>
      <p class="text-body-2 mb-4" style="opacity: 0.85">{{ step.body }}</p>

      <v-btn v-if="step.to" color="primary" size="small" :to="step.to">
        {{ step.action }}
      </v-btn>
      <v-btn v-else size="small" variant="tonal" disabled>{{ step.action }}</v-btn>
    </v-card>
  </v-container>
</template>
